'use client';

import {useAuth} from "@/features/auth/hooks/use-auth";
import {createContext, useCallback, useEffect} from "react";
import {useRouter} from "next/navigation";
import type { TUser } from '@/features/auth/types/t-user';

const UserContext = createContext<(TUser & { role?: { permissions_names?: string[] } }) | null>(null);

export function UserContextProvider ({ children }: {
  children: React.ReactNode;
}) {
  const router = useRouter();

  const forceLogout = useCallback(async () => {
    localStorage.removeItem('token');
    router.replace('/login');
  }, [router]);

  const { user, isLoading } = useAuth();

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem('client_logger_user_id', String(user.id));
    } else {
      localStorage.removeItem('client_logger_user_id');
    }
  }, [user?.id]);

  useEffect(() => {
    const syncAuth = async () => {
      const token = localStorage.getItem('token');

      if (!token) return;

      // проверяем есть ли cookie
      const hasCookie = document.cookie
        .split('; ')
        .some((c) => c.startsWith('token='));

      // если token в localStorage есть, а cookie нет — пытаемся восстановить куку
      if (!hasCookie) {
        try {
          const res = await fetch('/api/auth/set-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
          if (!res.ok) {
            forceLogout();
          }
        } catch {
          forceLogout();
        }
      }
    };

    syncAuth();
  }, [forceLogout]);

  if (!isLoading && typeof user === 'undefined') {
    router.push('/login');

    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <UserContext.Provider value={user}>
      {children}
    </UserContext.Provider>
  );
}

export default UserContext;